import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Sparkles, Heart, Mail, ArrowRight, Star, Cloud, Sun, Send, ShoppingCart, Headphones, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  green:  { bg: "#7CE7C0", accent: "#2BB785" },
  red:    { bg: "#FF8A8A", accent: "#E03E3E" },
  purple: { bg: "#B79CFF", accent: "#7A4DE0" },
  yellow: { bg: "#FFE16A", accent: "#F2A93B" },
  blue:   { bg: "#7ED2FF", accent: "#2BA4E0" },
};

const LOGO_MAIN  = `/logos/logo2.png`;
const LOGO_ALT   = `/logos/logo.png`;
const LOGO_BEAR  = `/logos/bear.png`;
const LOGO_YAWNY = `/logos/yawny.png`;
const FERRIS_BASE  = `/logos/fw_base.png`;
const FERRIS_WHEEL = `/logos/fw_wheel.png`;
const CHAR_EMMA    = `/logos/emma.png`;
const CHAR_PARENTS = `/logos/parents.png`;
const CHAR_GRANDPA = `/logos/grandpa.png`;
const CHAR_CAT     = `/logos/cat.png`;
const CHAR_BEE     = `/logos/bee.png`;
const CHAR_BOYWAVE = `/logos/boywave.png`;
const CHAR_BOYARMS = `/logos/boyarms.png`;
const CHAR_GIRLYEL = `/logos/girlyellow.png`;
const CHAR_BEARSIT = `/logos/bearsit.png`;
const CHAR_BEARLIE = `/logos/bearlie.png`;
const CHAR_BEARSTAND = `/logos/bearstand.png`;
const CHAR_BEARDANCE = `/logos/beardance.png`;
const CHAR_GRANDMA  = `/logos/grandma.png`;
const CHAR_SQUIRREL = `/logos/squirrel.png`;
const CHAR_FISH     = `/logos/fish.png`;

const Nav = () => (
  <nav className="sticky top-0 z-40 backdrop-blur-md bg-[rgba(191,233,255,0.85)] border-b-[3px] border-[#1B1140]">
    <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-2.5">
      <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
        <img
          src={LOGO_MAIN}
          alt="Emma and Teddy Adventures"
          data-testid="nav-logo-img"
          className="h-16 sm:h-20 w-auto wiggle-hover"
          style={{ mixBlendMode: "multiply" }}
        />
      </a>
      <div className="hidden md:flex items-center gap-7 font-display font-medium text-[#1B1140]">
        <a href="#books" data-testid="nav-books" className="hover:text-[#E03E3E] transition">The Books</a>
        <a href="#reviews" data-testid="nav-reviews" className="hover:text-[#E03E3E] transition">Reviews</a>
        <a href="#about" data-testid="nav-about" className="hover:text-[#E03E3E] transition">About</a>
        <a href="#readalong" data-testid="nav-readalong" className="hover:text-[#E03E3E] transition">Read-Along</a>
        <a href="#contact" data-testid="nav-contact" className="hover:text-[#E03E3E] transition">Contact</a>
      </div>
      <a
        href="https://www.amazon.com/stores/Yawny-and-Yammy/author/B009474L5U"
        target="_blank" rel="noreferrer"
        data-testid="nav-cta-shop"
        className="btn-crayon btn-coral text-sm md:text-base"
      >
        <ShoppingCart size={16} /> Shop on Amazon
      </a>
    </div>
  </nav>
);

const FerrisWheel = () => {
  const cars = [
    { color: "#FFD23F", emoji: "🐻" },
    { color: "#FF7AC6", emoji: "🐰" },
    { color: "#5BE0B3", emoji: "🦊" },
    { color: "#7ED2FF", emoji: "🐱" },
    { color: "#9A6CFF", emoji: "🐼" },
    { color: "#FF9F1C", emoji: "🦄" },
    { color: "#FF5C5C", emoji: "🐶" },
    { color: "#FFD23F", emoji: "🐸" },
  ];
  const N = cars.length;
  const RIM_OFFSET = 50;
  return (
    <div className="hidden md:block absolute top-2 right-6 lg:right-12 w-64 h-64 lg:w-80 lg:h-80 pointer-events-none z-0" data-testid="hero-ferris-wheel">
      <img src={FERRIS_BASE} alt="" className="absolute inset-0 w-full h-full object-contain" />
      <div className="fw-stage">
        <div className="fw-wheel">
          <div className="fw-rim" />
          {Array.from({ length: N }).map((_, i) => (
            <div
              key={`spoke-${i}`}
              className="fw-spoke"
              style={{ transform: `rotate(${(360 / N) * i}deg)` }}
            />
          ))}
          {cars.map((c, i) => {
            const angleDeg = (360 / N) * i;
            const angleRad = (angleDeg * Math.PI) / 180;
            const x = 50 + RIM_OFFSET * Math.cos(angleRad);
            const y = 50 + RIM_OFFSET * Math.sin(angleRad);
            return (
              <div
                key={`car-${i}`}
                className="fw-car"
                style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
              >
                <div className="fw-car-inner" style={{ background: c.color }}>
                  <span style={{ fontSize: "14px" }}>{c.emoji}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-12 pb-20 md:pt-16 md:pb-28">
    {/* Floating bear mascot */}
    <img
      src={LOGO_BEAR}
      alt="Teddy bear mascot"
      data-testid="hero-bear-decor"
      className="absolute top-6 left-[2%] sm:left-[4%] w-32 sm:w-44 z-10 float-bob -rotate-[6deg]"
    />
    {/* Bee buzzing in top-right */}
    <img
      src={CHAR_BEE}
      alt="Buzzing bee"
      data-testid="hero-bee"
      className="hidden sm:block absolute top-4 right-[20%] w-16 lg:w-20 float-bob-slow rotate-[15deg] z-10"
    />
    {/* Decorative spinning color burst */}
    <div className="absolute -top-10 -right-10 w-64 h-64 star-burst spin-slow" />

    <div className="relative max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <div className="inline-flex items-center gap-2 bg-white border-[3px] border-[#1B1140] rounded-full px-4 py-1.5 mb-5 shadow-[3px_3px_0_0_#1B1140]">
          <Sparkles size={16} className="text-[#F2A93B]" />
          <span className="font-display text-sm font-semibold text-[#1B1140]">A growing series. 3 books out now!</span>
        </div>
        <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-[#1B1140] leading-[0.95]">
          Big lessons,<br/>
          <span className="text-[#E03E3E]">tiny</span>{" "}
          <span className="relative inline-block">
            <span className="font-hand text-[#2BA4E0] text-6xl sm:text-7xl lg:text-8xl">friends</span>
            <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 200 14" fill="none">
              <path d="M2 8 Q 50 -2 100 6 T 198 8" stroke="#FFD23F" strokeWidth="4" strokeLinecap="round" fill="none"/>
            </svg>
          </span>.
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-[#3F2E72] max-w-xl font-body">
          Follow Emma and her beloved bear Teddy through heartwarming, hand-drawn picture books that teach kids
          positivity, good habits, focus and kindness — one cozy adventure at a time.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a href="#books" data-testid="hero-cta-explore" className="btn-crayon btn-coral">
            <BookOpen size={18} /> Explore the books
          </a>
          <a href="#newsletter" data-testid="hero-cta-subscribe" className="btn-crayon btn-ghost">
            <Mail size={18} /> Get new release alerts
          </a>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <div className="flex">
            {[1,2,3,4,5].map(i => <Star key={i} size={18} fill="#F2A93B" className="text-[#F2A93B]" />)}
          </div>
          <span className="font-body text-[#3F2E72] text-sm">Loved by parents &amp; little readers on Amazon</span>
        </div>
      </div>

      {/* Stacked book covers */}
      <div className="relative h-[460px] sm:h-[520px]">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[440px] h-[420px]">
            <img
              src="https://m.media-amazon.com/images/I/71RZoAppWuL._SY342_.jpg"
              alt="Emma and Teddy: The Magic of Focus"
              data-testid="hero-book-3"
              className="absolute left-0 top-4 w-40 sm:w-48 rounded-xl border-[3px] border-[#1B1140] shadow-[8px_8px_0_0_#1B1140] -rotate-[12deg] float-bob"
            />
            <img
              src="https://m.media-amazon.com/images/I/71vGEae7i9L._SY342_.jpg"
              alt="Emma and Teddy: The Beauty of Positive Thinking"
              data-testid="hero-book-1"
              className="absolute right-0 top-4 w-40 sm:w-48 rounded-xl border-[3px] border-[#1B1140] shadow-[8px_8px_0_0_#1B1140] rotate-[12deg] float-bob"
            />
            <img
              src="https://m.media-amazon.com/images/I/81Xcfe53cWL._SY342_.jpg"
              alt="Emma and Teddy: The Power of Habits"
              data-testid="hero-book-2"
              className="absolute left-4 bottom-0 w-36 sm:w-44 rounded-xl border-[3px] border-[#1B1140] shadow-[8px_8px_0_0_#1B1140] -rotate-[4deg] float-bob-slow"
            />
          </div>
        </div>
        <div className="absolute bottom-2 right-0 flex items-center gap-2 font-hand text-2xl text-[#1B1140] rotate-[-4deg]">
          <span>read them all!</span>
          <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
            <path d="M2 4 Q 25 30 46 14" stroke="#1B1140" strokeWidth="3" strokeLinecap="round" fill="none"/>
            <path d="M40 8 L 46 14 L 40 20" stroke="#1B1140" strokeWidth="3" strokeLinecap="round" fill="none"/>
          </svg>
        </div>
      </div>
    </div>
  </section>
);

const ScribbleDivider = () => <div className="scribble-divider opacity-50" />;

const BookCard = ({ book }) => {
  const c = COLORS[book.color] || COLORS.sunshine;
  const isComing = book.status === "coming_soon";
  return (
    <article
      data-testid={`book-card-${book.number}`}
      className="sticker p-5 sm:p-6"
      style={{ background: c.bg }}
    >
      <span className="sticker-tape" style={{ background: c.accent, color: "#fff" }}>
        {isComing ? "Coming Soon" : `Book ${book.number}`}
      </span>
      <div className="aspect-[3/4] rounded-xl bg-white border-[3px] border-[#1B1140] overflow-hidden mb-4 flex items-center justify-center">
        {book.cover ? (
          <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
        ) : (
          <div className="text-center p-6">
            <div className="text-5xl mb-2">✨</div>
            <div className="font-hand text-2xl text-[#1B1140]">cover reveal soon</div>
          </div>
        )}
      </div>
      <h3 className="font-display font-bold text-xl text-[#1B1140] leading-tight">
        <span className="opacity-60">The </span>{book.title.replace(/^The /, "")}
      </h3>
      <p className="mt-2 text-sm text-[#3F2E72] font-body min-h-[80px]">{book.description}</p>
      {book.amazon ? (
        <a
          href={book.amazon}
          target="_blank" rel="noreferrer"
          data-testid={`book-buy-${book.number}`}
          className="btn-crayon mt-4 text-sm w-full justify-center"
          style={{ background: c.accent, color: "white" }}
        >
          Buy on Amazon <ArrowRight size={16} />
        </a>
      ) : (
        <div data-testid={`book-soon-${book.number}`} className="btn-crayon btn-ghost mt-4 text-sm w-full justify-center cursor-default" style={{opacity:0.85}}>
          <Sparkles size={16} /> Notify me
        </div>
      )}
    </article>
  );
};

const Books = ({ books }) => (
  <section id="books" className="py-20 relative">
    {/* Emma & Teddy waving above the books */}
    <img
      src={CHAR_EMMA}
      alt="Emma & Teddy waving"
      data-testid="books-emma-decor"
      className="hidden md:block absolute -top-8 right-[4%] w-40 lg:w-48 float-bob z-10 rotate-[6deg] pointer-events-none"
    />
    {/* Sleeping cat (Honey) in bottom-left */}
    <img
      src={CHAR_CAT}
      alt="Honey the cat"
      data-testid="books-cat-decor"
      className="hidden md:block absolute bottom-4 left-[3%] w-28 lg:w-32 float-bob-slow -rotate-[8deg] z-10 pointer-events-none"
    />
    <div className="max-w-6xl mx-auto px-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-hand text-3xl text-[#E03E3E]">the series</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#1B1140] leading-tight">
            The Emma &amp; Teddy <span className="wavy decoration-[#2BA4E0]">Adventures</span>
          </h2>
          <p className="mt-3 font-body text-[#3F2E72] max-w-xl">
            Heartwarming, hand-drawn picture books that wrap a gentle life lesson in a world little ones adore — and the series keeps growing.
          </p>
        </div>
        <div className="font-display text-[#1B1140] text-sm bg-white border-[3px] border-[#1B1140] rounded-2xl px-4 py-3 shadow-[3px_3px_0_0_#1B1140]">
          <span className="font-bold text-2xl text-[#E03E3E]">3</span> out now · <span className="font-bold text-2xl text-[#2BA4E0]">more</span> on the way
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
        {books.map(b => <BookCard key={b.number} book={b} />)}
      </div>
    </div>
  </section>
);

const REVIEW_BG = ["#FFD23F", "#7ED2FF", "#FFA0D4", "#7CE7C0", "#B79CFF", "#FF9F1C"];

const ReviewCard = ({ review, index, books }) => {
  const book = books.find(b => b.number === review.book_number);
  const bg = REVIEW_BG[index % REVIEW_BG.length];
  return (
    <article
      data-testid={`review-card-${index}`}
      className="sticker p-5 sm:p-6 relative"
      style={{ background: bg, transform: `rotate(${index % 2 === 0 ? -0.6 : 0.6}deg)` }}
    >
      <Quote size={28} className="text-[#1B1140] opacity-30 absolute top-4 right-4" />
      <div className="flex items-center gap-1 mb-3">
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} size={18} fill="#F2A93B" className="text-[#F2A93B]" />
        ))}
      </div>
      {review.title && (
        <h3 className="font-display font-bold text-lg text-[#1B1140] leading-tight mb-2">{review.title}</h3>
      )}
      <p className="font-body text-[#1B1140] text-base">"{review.text}"</p>
      <div className="mt-4 pt-3 border-t-2 border-dashed border-[#1B1140]/30 flex items-center justify-between gap-3 text-sm">
        <div className="font-display font-semibold text-[#1B1140]">
          — {review.reviewer_name}
          {review.verified && <span className="ml-2 text-xs bg-[#1B1140] text-white px-2 py-0.5 rounded-full font-body">verified</span>}
        </div>
        {book && (
          <div className="font-hand text-base text-[#1B1140]/80 truncate max-w-[160px]">Book {book.number}</div>
        )}
      </div>
    </article>
  );
};

const Reviews = ({ books }) => {
  const [data, setData] = useState({ reviews: [], total: 0, average_rating: null });

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data.reviews.length) return null;

  return (
    <section id="reviews" className="py-20 relative">
      {/* Boy waving hello to readers */}
      <img src={CHAR_BOYWAVE} alt="Boy waving" data-testid="reviews-boywave" className="hidden md:block absolute top-6 right-[4%] w-32 lg:w-40 float-bob z-10 pointer-events-none" />
      {/* Standing teddy bear */}
      <img src={CHAR_BEARSTAND} alt="Teddy waving" data-testid="reviews-bearstand" className="hidden md:block absolute bottom-8 left-[3%] w-28 lg:w-36 float-bob-slow -rotate-[6deg] z-10 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="font-hand text-3xl text-[#E03E3E]">what readers say</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#1B1140] leading-tight">
              Loved by little <span className="wavy decoration-[#FFD23F]">readers</span>
            </h2>
            <p className="mt-3 font-body text-[#1B1140] max-w-xl">
              Real reviews from Amazon — and we'll add new ones as they come in.
            </p>
          </div>
          {data.average_rating != null && (
            <div data-testid="reviews-stats" className="font-display text-[#1B1140] text-sm bg-white border-[3px] border-[#1B1140] rounded-2xl px-4 py-3 shadow-[3px_3px_0_0_#1B1140] flex items-center gap-3">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={20} fill={i < Math.round(data.average_rating) ? "#F2A93B" : "transparent"} className="text-[#F2A93B]" />
                ))}
              </div>
              <div>
                <div className="font-bold text-2xl">{data.average_rating.toFixed(1)}</div>
                <div className="text-xs opacity-70">{data.total} review{data.total === 1 ? "" : "s"}</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {data.reviews.map((r, i) => <ReviewCard key={r.id || i} review={r} index={i} books={books} />)}
        </div>

        <div className="mt-10 text-center">
          <a
            href="https://www.amazon.com/stores/Yawny-and-Yammy/author/B009474L5U"
            target="_blank" rel="noreferrer"
            data-testid="reviews-cta-amazon"
            className="btn-crayon btn-coral"
          >
            <Star size={16} fill="white" /> Leave a review on Amazon
          </a>
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section id="about" className="py-20 bg-gradient-to-br from-[#FFD23F] via-[#FF9F1C] to-[#FF7AC6] border-y-[3px] border-[#1B1140] relative overflow-hidden">
    <div className="absolute -left-12 top-10 w-40 h-40 rounded-full bg-[#5BE0B3]/60 blur-2xl" />
    <div className="absolute right-10 bottom-10 w-52 h-52 rounded-full bg-[#9A6CFF]/60 blur-2xl" />
    {/* Mom & Dad floating in the corner */}
    <img
      src={CHAR_PARENTS}
      alt="Mom and Dad"
      data-testid="about-parents-decor"
      className="hidden md:block absolute bottom-2 right-[3%] w-40 lg:w-52 float-bob-slow z-10 pointer-events-none"
    />
    {/* Cute squirrel reading along */}
    <img src={CHAR_SQUIRREL} alt="Squirrel" data-testid="about-squirrel" className="hidden md:block absolute top-6 right-[3%] w-24 lg:w-32 float-bob -rotate-[8deg] z-10 pointer-events-none" />
    {/* Jumping fish */}
    <img src={CHAR_FISH} alt="Fish" data-testid="about-fish" className="hidden sm:block absolute bottom-6 left-[4%] w-16 lg:w-20 float-bob-slow rotate-[10deg] z-10 pointer-events-none" />
    <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-[320px,1fr] gap-10 items-center relative">
      <div className="relative w-fit mx-auto">
        <img
          src={LOGO_YAWNY}
          alt="Yawny and Yammy Productions"
          data-testid="yawny-yammy-logo"
          className="relative w-72 sm:w-80 wiggle-hover"
        />
        <img
          src="https://m.media-amazon.com/images/S/amzn-author-media-prod/gug2m60t48l93ga55hjaspvgiq._SY600_._SL200_._PQ50_._FMwebp_.jpg"
          alt="Yawny and Yammy"
          data-testid="author-photo"
          className="absolute -bottom-4 -right-2 w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-[4px] border-[#1B1140] shadow-[4px_4px_0_0_#1B1140] rotate-6"
        />
      </div>
      <div>
        <div className="font-hand text-3xl text-white drop-shadow-[2px_2px_0_#1B1140]">message to the readers</div>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#1B1140] leading-tight">
          We are <span className="text-white drop-shadow-[3px_3px_0_#1B1140]">Yawny &amp; Yammy</span>
        </h2>
        <p className="mt-5 font-body text-lg text-[#1B1140] max-w-2xl">
          We are the creators of the <em>Emma and Teddy Adventures</em> series — a heartwarming collection of children's books designed to encourage kindness, confidence, imagination, and positive thinking.
        </p>
        <p className="mt-3 font-body text-lg text-[#1B1140]/85 max-w-2xl">
          Our stories follow Emma and her beloved teddy bear, Teddy, as they explore the world, meet new friends, and discover gentle life lessons along the way. Each book is written with families in mind — meaningful messages in a fun, colorful, easy-to-love style. We hope to inspire children to see the good in everyday moments, believe in themselves, and carry kindness wherever they go.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="https://www.amazon.com/stores/Yawny-and-Yammy/author/B009474L5U" target="_blank" rel="noreferrer" data-testid="about-amazon-author" className="btn-crayon btn-sky">
            <BookOpen size={16} /> Visit our Amazon page
          </a>
          <a href="#contact" data-testid="about-contact" className="btn-crayon btn-ghost">
            <Mail size={16} /> Say hello
          </a>
        </div>
      </div>
    </div>
  </section>
);

const ReadAlong = () => (
  <section id="readalong" className="py-20 relative">
    {/* Grandpa smiling next to read-along section */}
    <img
      src={CHAR_GRANDPA}
      alt="Grandpa"
      data-testid="readalong-grandpa-decor"
      className="hidden md:block absolute bottom-2 left-[3%] w-32 lg:w-40 float-bob z-10 pointer-events-none"
    />
    {/* Bee floating around */}
    <img
      src={CHAR_BEE}
      alt="Bee"
      data-testid="readalong-bee-decor"
      className="hidden sm:block absolute top-8 right-[6%] w-14 lg:w-16 float-bob-slow rotate-[-10deg] z-10 pointer-events-none"
    />
    {/* Sleepy teddy bear — perfect cozy read-along vibe */}
    <img src={CHAR_BEARLIE} alt="Sleepy teddy" data-testid="readalong-bearlie" className="hidden md:block absolute bottom-4 right-[4%] w-36 lg:w-48 float-bob-slow z-10 pointer-events-none" />
    <div className="max-w-4xl mx-auto px-5 text-center">
      <div className="inline-flex items-center gap-2 bg-[#87D6A7] border-[3px] border-[#1B1140] rounded-full px-4 py-1.5 shadow-[3px_3px_0_0_#1B1140]">
        <Headphones size={16} /> <span className="font-display font-semibold">Read-Along · Coming Soon</span>
      </div>
      <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl text-[#1B1140]">
        A cozy voice for every page
      </h2>
      <p className="mt-4 font-body text-lg text-[#3F2E72] max-w-2xl mx-auto">
        We're recording read-along audio for the whole series so kids can listen, sing along, and follow each adventure on their own. Sign up below and you'll be the very first to know when it lands.
      </p>
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        {[
          { icon: <Sun size={26}/>, t: "Warm narration", s: "Perfect for bedtime & quiet time" },
          { icon: <Cloud size={26}/>, t: "Page-turn cues", s: "Little ones never lose their place" },
          { icon: <Sparkles size={26}/>, t: "Sound effects", s: "Gentle music & playful sounds" },
        ].map((b, i) => (
          <div key={i} className="sticker p-5 text-left" style={{ background: i===0 ? "#FFE9B3" : i===1 ? "#D6ECF8" : "#FBDCE7" }}>
            <div className="w-10 h-10 rounded-full bg-white border-[3px] border-[#1B1140] flex items-center justify-center">{b.icon}</div>
            <div className="mt-3 font-display font-bold text-lg text-[#1B1140]">{b.t}</div>
            <div className="font-body text-sm text-[#3F2E72]">{b.s}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email, name: name || null });
      toast.success(data.message || "Subscribed!");
      setEmail(""); setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Please try again.");
    } finally { setLoading(false); }
  };

  return (
  <section id="newsletter" className="py-20 relative">
      {/* Floating Emma & Teddy banner logo */}
      <img
        src={LOGO_ALT}
        alt="Emma and Teddy Adventures"
        data-testid="newsletter-floating-logo"
        className="hidden md:block absolute -top-2 right-[6%] w-44 lg:w-52 rotate-[8deg] float-bob-slow z-10 pointer-events-none"
      />
      {/* Girl in yellow dress */}
      <img src={CHAR_GIRLYEL} alt="Friendly reader" data-testid="newsletter-girlyellow" className="hidden md:block absolute bottom-4 left-[3%] w-28 lg:w-36 float-bob z-10 pointer-events-none" />
      {/* Dancing teddy bear */}
      <img src={CHAR_BEARDANCE} alt="Dancing teddy" data-testid="newsletter-beardance" className="hidden sm:block absolute -bottom-2 right-[2%] w-24 lg:w-32 float-bob z-10 pointer-events-none rotate-[8deg]" />
      <div className="max-w-3xl mx-auto px-5">
        <div className="sticker p-7 sm:p-10" style={{ background: "#FFE9B3" }}>
          <span className="sticker-tape">join the adventure</span>
          <div className="text-center">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1B1140]">
              Get new release alerts <span className="font-hand text-[#E03E3E]">straight to your inbox</span>
            </h2>
            <p className="mt-3 font-body text-[#3F2E72]">
              No spam — just a friendly note when the next Emma &amp; Teddy book is ready.
            </p>
          </div>
          <form onSubmit={submit} className="mt-7 grid sm:grid-cols-[1fr,1.4fr,auto] gap-3">
            <input
              data-testid="newsletter-name"
              className="crayon" placeholder="Your name (optional)"
              value={name} onChange={e=>setName(e.target.value)}
            />
            <input
              data-testid="newsletter-email"
              type="email" required className="crayon" placeholder="you@example.com"
              value={email} onChange={e=>setEmail(e.target.value)}
            />
            <button data-testid="newsletter-submit" disabled={loading} className="btn-crayon btn-coral justify-center">
              {loading ? "Sending..." : (<><Send size={16}/> Subscribe</>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[#7ED2FF] to-[#9A6CFF] border-y-[3px] border-[#1B1140] relative overflow-hidden">
      {/* Boy with open arms welcoming notes */}
      <img src={CHAR_BOYARMS} alt="Friendly fan" data-testid="contact-boyarms" className="hidden md:block absolute bottom-2 left-[3%] w-32 lg:w-44 float-bob z-10 pointer-events-none" />
      {/* Grandma reader */}
      <img src={CHAR_GRANDMA} alt="Grandma reader" data-testid="contact-grandma" className="hidden md:block absolute bottom-2 right-[3%] w-32 lg:w-44 float-bob-slow z-10 pointer-events-none" />
      <div className="max-w-3xl mx-auto px-5 relative z-20">
        <div className="text-center">
          <div className="font-hand text-3xl text-white drop-shadow-[2px_2px_0_#1B1140]">say hello</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#1B1140]">Get in touch</h2>
          <p className="mt-3 font-body text-[#1B1140] max-w-xl mx-auto">
            Parents, teachers, librarians, little fans — we love hearing from you. Drop us a note!
          </p>
        </div>
        <form onSubmit={submit} className="mt-9 grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="contact-name" required className="crayon" placeholder="Your name"
              value={form.name} onChange={e=>setForm({...form, name: e.target.value})}
            />
            <input
              data-testid="contact-email" type="email" required className="crayon" placeholder="Your email"
              value={form.email} onChange={e=>setForm({...form, email: e.target.value})}
            />
          </div>
          <textarea
            data-testid="contact-message" required rows={5} className="crayon" placeholder="Your message..."
            value={form.message} onChange={e=>setForm({...form, message: e.target.value})}
          />
          <div className="flex justify-center">
            <button data-testid="contact-submit" disabled={loading} className="btn-crayon btn-coral">
              {loading ? "Sending..." : (<><Send size={16}/> Send message</>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="py-10 bg-[#1B1140] text-[#FFF8EC]">
    <div className="max-w-6xl mx-auto px-5 grid sm:grid-cols-3 gap-8">
      <div>
        <div className="flex items-center gap-3">
          <img src={LOGO_BEAR} alt="Teddy" className="w-14 h-14" />
          <div>
            <div className="font-display font-bold text-lg">Emma &amp; Teddy</div>
            <div className="font-hand text-sm opacity-80 -mt-1">adventures</div>
          </div>
        </div>
        <p className="mt-3 font-body text-sm opacity-80">A heartwarming children's book series by Yawny &amp; Yammy. Big lessons. Tiny friends.</p>
      </div>
      <div>
        <div className="font-display font-bold mb-3">Explore</div>
        <ul className="space-y-2 text-sm opacity-90">
          <li><a href="#books" className="hover:underline">The Books</a></li>
          <li><a href="#about" className="hover:underline">About the Authors</a></li>
          <li><a href="#readalong" className="hover:underline">Read-Along</a></li>
          <li><a href="#contact" className="hover:underline">Contact</a></li>
        </ul>
      </div>
      <div>
        <div className="font-display font-bold mb-3">Buy the books</div>
        <ul className="space-y-2 text-sm opacity-90">
          <li><a target="_blank" rel="noreferrer" href="https://a.co/d/03duP0PQ" className="hover:underline">Book 1 — Positive Thinking</a></li>
          <li><a target="_blank" rel="noreferrer" href="https://a.co/d/07DWDKkP" className="hover:underline">Book 2 — Power of Habits</a></li>
          <li><a target="_blank" rel="noreferrer" href="https://a.co/d/010UM6dI" className="hover:underline">Book 3 — Magic of Focus</a></li>
          <li><a target="_blank" rel="noreferrer" href="https://www.amazon.com/stores/Yawny-and-Yammy/author/B009474L5U" className="hover:underline">Amazon author store →</a></li>
        </ul>
      </div>
    </div>
    <div className="max-w-6xl mx-auto px-5 mt-8 pt-6 border-t border-white/15 text-sm opacity-75 flex flex-col sm:flex-row gap-2 justify-between">
      <div>© {new Date().getFullYear()} Yawny &amp; Yammy · emmateddy.com</div>
      <div className="font-hand text-lg">made with <Heart size={14} className="inline -mt-1" fill="#FF7B6B" stroke="#FF7B6B" /> for little readers</div>
    </div>
  </footer>
);

export default function Home() {
  const [books, setBooks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/books`).then(r => setBooks(r.data.books || [])).catch(() => setBooks([]));
  }, []);

  return (
    <div data-testid="home-page" className="paper-grain">
      <Nav />
      <Hero />
      <Books books={books} />
      <Reviews books={books} />
      <About />
      <ReadAlong />
      <Newsletter />
      <Contact />
      <Footer />
    </div>
  );
}
